import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';

import {
  CellContext,
  ColorComponent,
  ColorOptions,
  GridData,
  NumericCellConfig,
} from '../../../models';
import { GridCellContentAlignment } from '../../../enums';
import { Constants } from '@grid/utils/constants';
import { isDecimalNumber, removeTrailingZeroDigits } from '@grid/utils/number';

@Component({
  selector: 'app-numeric-edit',
  templateUrl: './numeric-edit.component.html',
  styleUrls: ['./numeric-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumericEditComponent
  extends ColorComponent<GridData>
  implements OnInit
{
  @Input({ required: true }) context!: CellContext<GridData>;

  @ViewChild('input', { static: true }) inputRef!: ElementRef;
  value!: number | null;
  isDirty = false;

  GridCellContentAlignment = GridCellContentAlignment;

  dataTestId: string;

  renderer = inject(Renderer2);

  ngOnInit(): void {
    this.dataTestId = this.getDataTestId();
    this.renderer.setAttribute(
      this.inputRef?.nativeElement,
      'data-testId',
      this.dataTestId
    );
  }

  get config(): NumericCellConfig {
    return this.context.config as NumericCellConfig;
  }

  get max(): number {
    return (
      (this.config?.options?.allowPercentage
        ? Constants.NumericOptions.maxPercentage
        : this.config?.options?.max) ?? Number.MAX_SAFE_INTEGER
    );
  }

  get min(): number {
    return (
      (this.config?.options?.allowPercentage
        ? Constants.NumericOptions.minPercentage
        : this.config?.options?.min) ?? Number.MIN_SAFE_INTEGER
    );
  }

  get decimalPrecision(): number {
    return this.config.options.decimalPrecision;
  }

  protected get contextColorOptions(): ColorOptions {
    return this.config?.options?.colorOptions;
  }

  onEndEdit(): void {
    if (!this.isDirty) {
      return;
    }

    const initialValue = this.convertToNumber(
      this.context.data[this.context.column.columnDef]
    );

    if (initialValue !== this.value) {
      let newValue = this.value;
      // prevent user from entering value smaller than min value
      if (this.min != null && this.value < this.min) {
        newValue = this.min;
      }

      // prevent user from entering value bigger than max value
      if (this.max != null && this.value > this.max) {
        newValue = this.max;
      }

      const isDecimal = newValue ? isDecimalNumber(newValue) : false;
      this.context.data[this.context.column.columnDef] = isDecimal
        ? removeTrailingZeroDigits(newValue, this.decimalPrecision)
        : newValue;
      this.context.cell.actions.onEdited();
    }
  }

  onChange($event: any): void {
    if (this.value === $event) {
      return;
    }

    this.value = this.convertToNumber($event);
    this.isDirty = true;
  }

  //#region Private methods

  private convertToNumber(value: string | number): number | null {
    if (typeof value === 'number') {
      return value;
    }

    return this.hasValue(value) ? Number(value) : null;
  }

  private hasValue(value: string): boolean {
    return value !== '' && value != null;
  }

  private getDataTestId(): string {
    return (
      'edit-' + this.context.column.header.name + '-' + this.context.row.index
    );
  }

  //#endregion
}
